import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, SearchIcon, PlusIcon, ChevronRightIcon, BuildingIcon, SmartphoneIcon, ShieldIcon, StoreIcon, UserIcon, Edit3Icon, WalletIcon, CheckIcon, XIcon, ShareIcon, CalendarIcon, CopyIcon, ArrowRightIcon, RefreshCwIcon, BellIcon, ChevronDownIcon, CheckCircleIcon, InfoIcon, FingerprintIcon } from 'lucide-react';
import { NotificationPanel } from './NotificationPanel';
interface SendMoneyScreenProps {
  onBack: () => void;
}
type Step = 'select_recipient' | 'select_bank' | 'add_beneficiary' | 'verify_beneficiary' | 'beneficiary_saved' | 'enter_amount' | 'mpin' | 'processing' | 'success';
interface Recipient {
  id: string;
  name: string;
  bank: string;
  accountNumber: string;
  icon: React.ElementType;
  color: string;
}
const RECENT_RECIPIENTS = [{
  id: '1',
  name: 'Asad Ali',
  initials: 'AA'
}, {
  id: '2',
  name: 'Fatima',
  initials: 'F'
}, {
  id: '3',
  name: 'Rashid',
  initials: 'RK'
}, {
  id: '4',
  name: 'Zaid',
  initials: 'Z'
}];
const ALL_BENEFICIARIES: Recipient[] = [{
  id: '1',
  name: 'Asad Ali',
  bank: 'Bank Alfalah',
  accountNumber: '0123 4567 8910',
  icon: BuildingIcon,
  color: '#F37024'
}, {
  id: '2',
  name: 'Fatima Zahra',
  bank: 'HBL',
  accountNumber: '9876 5432 1098',
  icon: WalletIcon,
  color: '#F37024'
}, {
  id: '3',
  name: 'Rashid Khan',
  bank: 'Meezan Bank',
  accountNumber: '5544 3322 1100',
  icon: ShieldIcon,
  color: '#F37024'
}, {
  id: '4',
  name: 'Metro Mart Corp',
  bank: 'Standard Chartered',
  accountNumber: '8877 6655 4433',
  icon: StoreIcon,
  color: '#F37024'
}, {
  id: '5',
  name: 'Zaid Khan',
  bank: 'UBL',
  accountNumber: '2211 4433 6655',
  icon: UserIcon,
  color: '#F37024'
}];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
const BANKS = ['Bank of Punjab', 'HBL', 'Meezan Bank', 'Bank Alfalah', 'UBL', 'Standard Chartered', 'MCB', 'Allied Bank', 'Faysal Bank', 'JS Bank'];
const RELATIONSHIPS = ['Family', 'Friend', 'Business', 'Other'];
let biometricCounter = 0;
export function SendMoneyScreen({
  onBack
}: SendMoneyScreenProps) {
  const [step, setStep] = useState<Step>('select_recipient');
  const [direction, setDirection] = useState(1);
  const [showNotifications, setShowNotifications] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState<Recipient[]>(ALL_BENEFICIARIES);
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [purpose, setPurpose] = useState('');
  const [showPurposeDropdown, setShowPurposeDropdown] = useState(false);
  const [mpin, setMpin] = useState('');
  const [biometricPhase, setBiometricPhase] = useState<'scanning' | 'verified' | 'entering_mpin'>('scanning');
  const [scanType, setScanType] = useState<'face' | 'thumb'>('face');
  const PURPOSES = ['Family Support', 'Bill Payment', 'Salary', 'Business Payment', 'Loan Repayment', 'Gift', 'Other'];
  // Add Beneficiary Form State
  const [newBeneficiaryName, setNewBeneficiaryName] = useState('');
  const [newBeneficiaryAccount, setNewBeneficiaryAccount] = useState('');
  const [accountInputType, setAccountInputType] = useState<'account' | 'iban'>('account');
  const [newBeneficiaryRelationship, setNewBeneficiaryRelationship] = useState('');
  const [newBeneficiaryBank, setNewBeneficiaryBank] = useState('');
  const [newBeneficiaryNickname, setNewBeneficiaryNickname] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [showRelationshipDropdown, setShowRelationshipDropdown] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const [addBeneficiaryPhase, setAddBeneficiaryPhase] = useState<'entry' | 'fetching' | 'details_fetched' | 'preparing_payment' | 'payment_details'>('entry');
  const [saveBeneficiary, setSaveBeneficiary] = useState(false);
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  const handleSelectRecipient = (recipient: Recipient) => {
    setSelectedRecipient(recipient);
    navigateTo('enter_amount');
  };
  useEffect(() => {
    if (step === 'processing') {
      const timer = setTimeout(() => {
        navigateTo('success');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [step]);
  useEffect(() => {
    if (step === 'mpin') {
      setMpin('');
      setBiometricPhase('scanning');
      const currentScanType = biometricCounter % 2 === 0 ? 'face' : 'thumb';
      setScanType(currentScanType);
      biometricCounter++;
      let timeout1: NodeJS.Timeout;
      let timeout2: NodeJS.Timeout;
      let interval: NodeJS.Timeout;
      timeout1 = setTimeout(() => {
        setBiometricPhase('verified');
        timeout2 = setTimeout(() => {
          navigateTo('processing');
        }, 800);
      }, 2000);
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
      };
    }
  }, [step]);
  const renderStep = () => {
    switch (step) {
      case 'select_recipient':
        return <motion.div key="select_recipient" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen bg-[#FFF7F3] pb-24" data-id="element-588">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 bg-[#FFF7F3] z-10" data-id="element-589">
              <button onClick={onBack} className="p-2 -ml-2 text-[#303030]" data-id="element-590">
                <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-591" />
              </button>
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-592" />
              <button onClick={() => setShowNotifications(true)} className="p-2 -mr-2 text-[#818285] relative" data-id="element-593">
                <BellIcon className="w-6 h-6" strokeWidth={2} data-id="element-594" />
                <div className="absolute top-1 right-0 w-2 h-2 bg-[#F37024] rounded-full" data-id="element-595" />
              </button>
            </div>

            <div className="px-6 mt-4 space-y-8" data-id="element-596">
              <div data-id="element-597">
                <h1 className="text-[28px] font-bold text-[#303030] tracking-tight mb-1" data-id="element-598">
                  Select Recipient
                </h1>
                <p className="text-[15px] text-[#661806]" data-id="element-599">
                  Choose where to send your funds
                </p>
              </div>

              {/* Search */}
              <div className="relative" data-id="element-600">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#818285]" data-id="element-601" />
                <input type="text" placeholder="Search name, bank or account number" className="w-full bg-[#F5EBE6] rounded-2xl py-4 pl-12 pr-4 text-[15px] text-[#303030] placeholder:text-[#818285] outline-none" data-id="element-602" />
              </div>

              {/* Recently Sent */}
              <div data-id="element-603">
                <h3 className="text-[13px] font-bold text-[#661806] tracking-wider uppercase mb-4" data-id="element-604">
                  Recently Sent
                </h3>
                <div className="flex gap-4 overflow-x-auto pb-2 -mx-6 px-6 hide-scrollbar" data-id="element-605">
                  <button onClick={() => navigateTo('select_bank')} className="flex flex-col items-center gap-2 min-w-[72px]" data-id="element-606">
                    <div className="w-[72px] h-[72px] rounded-full bg-[#F37024] flex items-center justify-center shadow-md" data-id="element-607">
                      <PlusIcon className="w-8 h-8 text-white" strokeWidth={2} data-id="element-608" />
                    </div>
                    <span className="text-[13px] font-medium text-[#661806]" data-id="element-609">
                      New
                    </span>
                  </button>
                  {RECENT_RECIPIENTS.map(rec => <button key={rec.id} className="flex flex-col items-center gap-2 min-w-[72px]" data-id="element-610">
                      <div className="w-[72px] h-[72px] rounded-full bg-[#F5CBA7] flex items-center justify-center" data-id="element-611">
                        <UserIcon className="w-8 h-8 text-[#661806]" strokeWidth={1.5} data-id="element-612" />
                      </div>
                      <span className="text-[13px] font-medium text-[#303030]" data-id="element-613">
                        {rec.name}
                      </span>
                    </button>)}
                </div>
              </div>

              {/* All Beneficiaries */}
              <div data-id="element-614">
                <h3 className="text-[13px] font-bold text-[#661806] tracking-wider uppercase mb-4" data-id="element-615">
                  All Beneficiaries
                </h3>
                <div className="space-y-3" data-id="element-616">
                  {beneficiaries.map(rec => {
                  const Icon = rec.icon;
                  return <button key={rec.id} onClick={() => handleSelectRecipient(rec)} className="w-full bg-white rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow" data-id="element-617">
                        <div className="w-12 h-12 rounded-full bg-[#FFF7F3] flex items-center justify-center shrink-0" data-id="element-618">
                          <Icon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-619" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-620">
                          <h4 className="text-[16px] font-bold text-[#303030] mb-0.5" data-id="element-621">
                            {rec.name}
                          </h4>
                          <p className="text-[13px] text-[#818285]" data-id="element-622">
                            {rec.bank} • {rec.accountNumber}
                          </p>
                        </div>
                        <ChevronRightIcon className="w-5 h-5 text-[#818285]" data-id="element-623" />
                      </button>;
                })}
                </div>
              </div>
            </div>
          </motion.div>;
      case 'select_bank':
        const filteredBanks = BANKS.filter(bank => bank.toLowerCase().includes(bankSearch.toLowerCase()));
        return <motion.div key="select_bank" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen bg-[#FFF7F3] flex flex-col" data-id="element-624">
            <div className="px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 bg-[#FFF7F3] z-10" data-id="element-625">
              <button onClick={() => {
              setBankSearch('');
              navigateTo('select_recipient', -1);
            }} className="p-2 -ml-2 text-[#303030]" data-id="element-626">
                <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-627" />
              </button>
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-628" />
              <button onClick={() => setShowNotifications(true)} className="p-2 -mr-2 text-[#818285] relative" data-id="element-629">
                <BellIcon className="w-6 h-6" strokeWidth={2} data-id="element-630" />
                <div className="absolute top-1 right-0 w-2 h-2 bg-[#F37024] rounded-full" data-id="element-631" />
              </button>
            </div>

            <div className="px-6 mt-4 flex-1 flex flex-col pb-8" data-id="element-632">
              <div className="mb-6" data-id="element-633">
                <h1 className="text-[28px] font-bold text-[#303030] tracking-tight mb-1" data-id="element-634">
                  Select Bank
                </h1>
                <p className="text-[15px] text-[#661806]" data-id="element-635">
                  Choose the recipient's bank
                </p>
              </div>

              {/* Search */}
              <div className="relative mb-6" data-id="element-636">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#818285]" data-id="element-637" />
                <input type="text" value={bankSearch} onChange={e => setBankSearch(e.target.value)} placeholder="Search bank name" className="w-full bg-[#F5EBE6] rounded-2xl py-4 pl-12 pr-4 text-[15px] text-[#303030] placeholder:text-[#818285] outline-none" autoFocus data-id="element-638" />
              </div>

              {/* Bank List */}
              <div className="space-y-2 flex-1 overflow-y-auto" data-id="element-639">
                {filteredBanks.map(bank => <button key={bank} onClick={() => {
                setNewBeneficiaryBank(bank);
                setNewBeneficiaryAccount('');
                setAccountInputType('account');
                setBankSearch('');
                setAddBeneficiaryPhase('entry');
                navigateTo('add_beneficiary');
              }} className="w-full bg-white rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow active:scale-[0.98]" data-id="element-640">
                    <div className="w-11 h-11 rounded-full bg-[#FDF3ED] flex items-center justify-center shrink-0" data-id="element-641">
                      <BuildingIcon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-642" />
                    </div>
                    <span className="text-[15px] font-semibold text-[#303030] flex-1 text-left" data-id="element-643">
                      {bank}
                    </span>
                    <ChevronRightIcon className="w-5 h-5 text-[#818285]" data-id="element-644" />
                  </button>)}
                {filteredBanks.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-645">
                    <SearchIcon className="w-10 h-10 text-[#818285]/40 mb-3" data-id="element-646" />
                    <p className="text-[15px] font-medium text-[#818285]" data-id="element-647">
                      No banks found
                    </p>
                    <p className="text-[13px] text-[#818285]/70 mt-1" data-id="element-648">
                      Try a different search term
                    </p>
                  </div>}
              </div>
            </div>
          </motion.div>;
      case 'add_beneficiary':
        return <motion.div key="add_beneficiary" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen bg-[#FFF7F3] flex flex-col" data-id="element-649">
            <div className="px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 bg-[#FFF7F3] z-10" data-id="element-650">
              <button onClick={() => {
              if (addBeneficiaryPhase === 'payment_details') {
                setAddBeneficiaryPhase('details_fetched');
              } else if (addBeneficiaryPhase === 'details_fetched') {
                setAddBeneficiaryPhase('entry');
                setNewBeneficiaryAccount('');
              } else {
                navigateTo('select_bank', -1);
              }
            }} className="p-2 -ml-2 text-[#303030]" data-id="element-651">
                <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-652" />
              </button>
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-653" />
              <button onClick={() => setShowNotifications(true)} className="p-2 -mr-2 text-[#818285] relative" data-id="element-654">
                <BellIcon className="w-6 h-6" strokeWidth={2} data-id="element-655" />
                <div className="absolute top-1 right-0 w-2 h-2 bg-[#F37024] rounded-full" data-id="element-656" />
              </button>
            </div>

            <div className="px-6 mt-4 flex-1 flex flex-col pb-8" data-id="element-657">
              <div className="mb-8" data-id="element-658">
                <h1 className="text-[28px] font-bold text-[#303030] tracking-tight mb-1" data-id="element-659">
                  {addBeneficiaryPhase === 'payment_details' ? 'Payment Details' : 'Add Beneficiary'}
                </h1>
                <p className="text-[15px] text-[#661806]" data-id="element-660">
                  {addBeneficiaryPhase === 'payment_details' ? 'Enter amount and purpose' : `Enter account details for ${newBeneficiaryBank}`}
                </p>
              </div>

              {/* Selected Bank Chip */}
              {addBeneficiaryPhase === 'entry' && <div className="flex items-center gap-3 bg-white rounded-2xl p-4 shadow-sm mb-6" data-id="element-661">
                  <div className="w-10 h-10 rounded-full bg-[#FDF3ED] flex items-center justify-center shrink-0" data-id="element-662">
                    <BuildingIcon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-663" />
                  </div>
                  <div className="flex-1" data-id="element-664">
                    <p className="text-[11px] font-bold text-[#818285] uppercase tracking-wider" data-id="element-665">
                      Selected Bank
                    </p>
                    <p className="text-[15px] font-bold text-[#303030]" data-id="element-666">
                      {newBeneficiaryBank}
                    </p>
                  </div>
                  <button onClick={() => navigateTo('select_bank', -1)} className="text-[13px] font-bold text-[#F37024]" data-id="element-667">
                    Change
                  </button>
                </div>}

              {/* Account / IBAN Tabs */}
              {addBeneficiaryPhase === 'entry' && <div className="flex gap-2 mb-6" data-id="element-668">
                  <button onClick={() => {
                setAccountInputType('account');
                setNewBeneficiaryAccount('');
              }} className={`flex-1 py-3 rounded-xl text-[14px] font-bold transition-all ${accountInputType === 'account' ? 'bg-[#661806] text-white shadow-md' : 'bg-[#F5EBE6] text-[#818285]'}`} data-id="element-669">
                    Account Number
                  </button>
                  <button onClick={() => {
                setAccountInputType('iban');
                setNewBeneficiaryAccount('');
              }} className={`flex-1 py-3 rounded-xl text-[14px] font-bold transition-all ${accountInputType === 'iban' ? 'bg-[#661806] text-white shadow-md' : 'bg-[#F5EBE6] text-[#818285]'}`} data-id="element-670">
                    IBAN
                  </button>
                </div>}

              {/* Single Input Field */}
              {(addBeneficiaryPhase === 'entry' || addBeneficiaryPhase === 'fetching') && <div className="mb-auto" data-id="element-671">
                  <p className="text-[13px] font-bold text-[#661806] mb-2" data-id="element-672">
                    {accountInputType === 'iban' ? 'Enter 24-digit IBAN' : 'Enter 16-digit Account Number'}
                  </p>
                  <div className="relative" data-id="element-673">
                    <BuildingIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#818285]" data-id="element-674" />
                    <input type="text" value={newBeneficiaryAccount} disabled={addBeneficiaryPhase === 'fetching'} onChange={e => {
                  const val = e.target.value;
                  let newValue = '';
                  let isComplete = false;
                  if (accountInputType === 'iban') {
                    newValue = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    if (newValue.length <= 24) setNewBeneficiaryAccount(newValue);
                    if (newValue.length === 24) isComplete = true;
                  } else {
                    newValue = val.replace(/\D/g, '');
                    if (newValue.length <= 16) setNewBeneficiaryAccount(newValue);
                    if (newValue.length === 16) isComplete = true;
                  }
                  if (isComplete) {
                    setAddBeneficiaryPhase('fetching');
                    setTimeout(() => {
                      setNewBeneficiaryName('Waqas Ahmed');
                      setAddBeneficiaryPhase('details_fetched');
                    }, 1500);
                  }
                }} placeholder={accountInputType === 'iban' ? 'e.g. PK36BOPM0000001234567890' : 'e.g. 0000001234567890'} maxLength={accountInputType === 'iban' ? 24 : 16} className="w-full bg-[#F5EBE6] rounded-2xl py-4 pl-12 pr-20 text-[15px] text-[#303030] placeholder:text-[#818285] outline-none tracking-wider font-medium disabled:opacity-70" autoFocus data-id="element-675" />
                    {/* Character count */}
                    <span className={`absolute right-12 top-1/2 -translate-y-1/2 text-[11px] font-bold ${accountInputType === 'iban' && newBeneficiaryAccount.length === 24 || accountInputType === 'account' && newBeneficiaryAccount.length === 16 ? 'text-[#22C55E]' : 'text-[#818285]'}`} data-id="element-676">
                      {newBeneficiaryAccount.length}/
                      {accountInputType === 'iban' ? 24 : 16}
                    </span>
                    {/* Info Icon */}
                    <button onClick={() => setShowAccountInfo(!showAccountInfo)} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#661806]/10 flex items-center justify-center" data-id="element-677">
                      <InfoIcon className="w-4 h-4 text-[#661806]" strokeWidth={2} data-id="element-678" />
                    </button>
                  </div>

                  {/* Fetching Loader */}
                  <AnimatePresence data-id="element-679">
                    {addBeneficiaryPhase === 'fetching' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="mt-6 flex flex-col items-center justify-center" data-id="element-680">
                        <motion.div animate={{
                    rotate: 360
                  }} transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="mb-3" data-id="element-681">
                          <RefreshCwIcon className="w-8 h-8 text-[#F37024]" data-id="element-682" />
                        </motion.div>
                        <p className="text-[14px] font-medium text-[#661806]" data-id="element-683">
                          Fetching account details...
                        </p>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Info Tooltip */}
                  <AnimatePresence data-id="element-684">
                    {showAccountInfo && addBeneficiaryPhase === 'entry' && <motion.div initial={{
                  opacity: 0,
                  y: -8,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -8,
                  scale: 0.95
                }} transition={{
                  duration: 0.2
                }} className="mt-3 bg-[#661806] rounded-2xl p-4 relative" data-id="element-685">
                        <div className="absolute -top-2 right-5 w-4 h-4 bg-[#661806] rotate-45 rounded-sm" data-id="element-686" />
                        <div className="flex items-start gap-3" data-id="element-687">
                          <InfoIcon className="w-5 h-5 text-[#F5CBA7] shrink-0 mt-0.5" strokeWidth={2} data-id="element-688" />
                          <div data-id="element-689">
                            {accountInputType === 'iban' ? <>
                                <p className="text-[13px] font-bold text-white mb-1.5" data-id="element-690">
                                  IBAN Format
                                </p>
                                <p className="text-[12px] text-white/80 leading-relaxed" data-id="element-691">
                                  IBAN must be exactly{' '}
                                  <span className="font-bold text-[#F5CBA7]" data-id="element-692">
                                    24 characters
                                  </span>{' '}
                                  starting with the country code (e.g. PK),
                                  followed by 2 check digits and 20 alphanumeric
                                  characters.
                                </p>
                                <p className="text-[11px] text-white/60 mt-2" data-id="element-693">
                                  Example: PK36BOPM0000001234567890
                                </p>
                              </> : <>
                                <p className="text-[13px] font-bold text-white mb-1.5" data-id="element-694">
                                  Account Number Format
                                </p>
                                <p className="text-[12px] text-white/80 leading-relaxed" data-id="element-695">
                                  Account number must be exactly{' '}
                                  <span className="font-bold text-[#F5CBA7]" data-id="element-696">
                                    16 digits
                                  </span>
                                  . You can find this on your bank statement,
                                  cheque book, or internet banking portal.
                                </p>
                                <p className="text-[11px] text-white/60 mt-2" data-id="element-697">
                                  Example: 0000001234567890
                                </p>
                              </>}
                          </div>
                        </div>
                        <button onClick={() => setShowAccountInfo(false)} className="absolute top-3 right-3" data-id="element-698">
                          <XIcon className="w-4 h-4 text-white/60" strokeWidth={2} data-id="element-699" />
                        </button>
                      </motion.div>}
                  </AnimatePresence>
                </div>}

              {/* Fetched Details & Save/Pay Buttons */}
              {addBeneficiaryPhase === 'details_fetched' && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-auto" data-id="element-700">
                  <div className="bg-white rounded-[24px] p-6 shadow-sm mb-8 relative overflow-hidden" data-id="element-701">
                    <div className="absolute top-0 left-0 w-1 h-full bg-[#22C55E]" data-id="element-702" />
                    <div className="flex items-start justify-between mb-4" data-id="element-703">
                      <div className="w-12 h-12 rounded-full bg-[#22C55E]/10 flex items-center justify-center" data-id="element-704">
                        <CheckCircleIcon className="w-6 h-6 text-[#22C55E]" strokeWidth={2} data-id="element-705" />
                      </div>
                      <div className="bg-[#FDF3ED] rounded-xl px-3 py-1.5" data-id="element-706">
                        <p className="text-[11px] font-bold text-[#661806]" data-id="element-707">
                          {newBeneficiaryBank}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-4" data-id="element-708">
                      <div data-id="element-709">
                        <p className="text-[11px] font-bold text-[#818285] uppercase tracking-wider mb-1" data-id="element-710">
                          Account Holder Name
                        </p>
                        <p className="text-[18px] font-bold text-[#303030]" data-id="element-711">
                          {newBeneficiaryName}
                        </p>
                      </div>
                      <div className="h-px bg-[#F5EBE6] w-full" data-id="element-712" />
                      <div data-id="element-713">
                        <p className="text-[11px] font-bold text-[#818285] uppercase tracking-wider mb-1" data-id="element-714">
                          {accountInputType === 'iban' ? 'IBAN' : 'Account Number'}
                        </p>
                        <p className="text-[15px] font-medium text-[#303030] tracking-wide" data-id="element-715">
                          {newBeneficiaryAccount}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-716">
                    <button onClick={() => {
                  setSaveBeneficiary(true);
                  setAddBeneficiaryPhase('preparing_payment');
                  setTimeout(() => {
                    setAddBeneficiaryPhase('payment_details');
                  }, 1000);
                }} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-[20px] shadow-md hover:bg-[#E06520] transition-colors flex items-center justify-center gap-2" data-id="element-717">
                      Save & Pay
                      <ArrowRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-718" />
                    </button>
                    <button onClick={() => {
                  setSaveBeneficiary(false);
                  setAddBeneficiaryPhase('preparing_payment');
                  setTimeout(() => {
                    setAddBeneficiaryPhase('payment_details');
                  }, 1000);
                }} className="w-full py-4 bg-transparent text-[#661806] border-2 border-[#661806] font-bold text-[16px] rounded-[20px] hover:bg-[#661806]/5 transition-colors flex items-center justify-center" data-id="element-719">
                      Just Pay
                    </button>
                  </div>
                </motion.div>}

              {/* Preparing Payment Loader */}
              {addBeneficiaryPhase === 'preparing_payment' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex-1 flex flex-col items-center justify-center" data-id="element-720">
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="mb-4" data-id="element-721">
                    <RefreshCwIcon className="w-10 h-10 text-[#F37024]" data-id="element-722" />
                  </motion.div>
                  <p className="text-[16px] font-bold text-[#303030]" data-id="element-723">
                    Preparing payment...
                  </p>
                </motion.div>}

              {/* Payment Details */}
              {addBeneficiaryPhase === 'payment_details' && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex-1 flex flex-col" data-id="element-724">
                  <div className="flex flex-col items-center mb-10" data-id="element-725">
                    <p className="text-[15px] font-medium text-[#661806] mb-4 uppercase tracking-wider" data-id="element-726">
                      Currency: PKR
                    </p>
                    <div className="flex items-center gap-4 border-b-2 border-[#F5EBE6] pb-2 min-w-[200px] justify-center" data-id="element-727">
                      <span className="text-[24px] font-bold text-[#818285]" data-id="element-728">
                        PKR
                      </span>
                      <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" className="text-[56px] font-bold text-[#661806] bg-transparent outline-none w-[200px] text-center placeholder:text-[#661806]/30" autoFocus data-id="element-729" />
                    </div>
                  </div>

                  <div className="mb-8" data-id="element-730">
                    <p className="text-[14px] font-medium text-[#661806] mb-3" data-id="element-731">
                      Quick Amount
                    </p>
                    <div className="flex gap-3" data-id="element-732">
                      {['1,000', '5,000', '10,000'].map(val => <button key={val} onClick={() => setAmount(val.replace(',', ''))} className="flex-1 bg-white py-3 rounded-xl text-[15px] font-bold text-[#303030] shadow-sm hover:bg-[#F5EBE6] transition-colors" data-id="element-733">
                          {val}
                        </button>)}
                    </div>
                  </div>

                  <div className="mb-auto relative z-20" data-id="element-734">
                    <div className="flex items-center justify-between mb-3" data-id="element-735">
                      <p className="text-[14px] font-medium text-[#661806]" data-id="element-736">
                        Purpose of Payment
                      </p>
                      <span className="text-[10px] font-bold text-[#F37024] bg-[#F37024]/10 px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-737">
                        Required
                      </span>
                    </div>
                    <div className="relative" data-id="element-738">
                      <button onClick={() => setShowPurposeDropdown(!showPurposeDropdown)} className="w-full bg-[#F5EBE6] rounded-2xl py-4 pl-4 pr-12 text-[15px] text-left text-[#303030] outline-none flex items-center" data-id="element-739">
                        {purpose || <span className="text-[#818285]" data-id="element-740">Select Purpose</span>}
                        <ChevronDownIcon className="absolute right-4 w-5 h-5 text-[#818285]" data-id="element-741" />
                      </button>
                      <AnimatePresence data-id="element-742">
                        {showPurposeDropdown && <motion.div initial={{
                      opacity: 0,
                      y: -10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -10
                    }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-[#F5EBE6] z-30 max-h-48 overflow-y-auto" data-id="element-743">
                            {PURPOSES.map(p => <button key={p} onClick={() => {
                        setPurpose(p);
                        setShowPurposeDropdown(false);
                      }} className="w-full text-left px-4 py-3 text-[15px] text-[#303030] hover:bg-[#F5EBE6] transition-colors border-b border-[#F5EBE6] last:border-0" data-id="element-744">
                                {p}
                              </button>)}
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>

                  <button onClick={() => {
                const newRecipient: Recipient = {
                  id: Date.now().toString(),
                  name: newBeneficiaryName,
                  bank: newBeneficiaryBank,
                  accountNumber: newBeneficiaryAccount,
                  icon: BuildingIcon,
                  color: '#F37024'
                };
                if (saveBeneficiary) {
                  setBeneficiaries([...beneficiaries, newRecipient]);
                }
                setSelectedRecipient(newRecipient);
                navigateTo('mpin');
              }} disabled={!amount || parseFloat(amount) <= 0 || !purpose} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-[20px] shadow-md hover:bg-[#E06520] transition-colors flex items-center justify-center gap-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-745">
                    Make Payment
                    <ArrowRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-746" />
                  </button>
                </motion.div>}
            </div>
          </motion.div>;
      case 'verify_beneficiary':
        return <motion.div key="verify_beneficiary" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen bg-[#FFF7F3] flex flex-col" data-id="element-747">
            <div className="px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 bg-[#FFF7F3] z-10" data-id="element-748">
              <button onClick={() => navigateTo('add_beneficiary', -1)} className="p-2 -ml-2 text-[#303030]" data-id="element-749">
                <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-750" />
              </button>
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-751" />
              <button onClick={() => setShowNotifications(true)} className="p-2 -mr-2 text-[#818285] relative" data-id="element-752">
                <BellIcon className="w-6 h-6" strokeWidth={2} data-id="element-753" />
                <div className="absolute top-1 right-0 w-2 h-2 bg-[#F37024] rounded-full" data-id="element-754" />
              </button>
            </div>

            <div className="px-6 mt-4 flex-1 flex flex-col pb-8" data-id="element-755">
              <div className="flex flex-col items-center mb-6" data-id="element-756">
                <div className="w-16 h-16 rounded-full bg-[#FDF3ED] flex items-center justify-center mb-4" data-id="element-757">
                  <ShieldIcon className="w-8 h-8 text-[#F37024]" strokeWidth={1.5} data-id="element-758" />
                </div>
                <h1 className="text-[24px] font-bold text-[#303030] tracking-tight text-center" data-id="element-759">
                  Verify Account
                </h1>
                <p className="text-[14px] text-[#818285] mt-1" data-id="element-760">
                  Please confirm the account details below
                </p>
              </div>

              <div className="bg-white rounded-[24px] p-6 shadow-sm mb-6" data-id="element-761">
                <div className="space-y-4" data-id="element-762">
                  <div data-id="element-763">
                    <p className="text-[11px] font-bold text-[#818285] uppercase tracking-wider mb-1" data-id="element-764">
                      Account Holder Name
                    </p>
                    <p className="text-[18px] font-bold text-[#303030]" data-id="element-765">
                      {newBeneficiaryName}
                    </p>
                  </div>
                  <div className="h-px bg-[#F5EBE6] w-full" data-id="element-766" />
                  <div className="flex justify-between items-center" data-id="element-767">
                    <div data-id="element-768">
                      <p className="text-[11px] font-bold text-[#818285] uppercase tracking-wider mb-1" data-id="element-769">
                        {accountInputType === 'iban' ? 'IBAN' : 'Account Number'}
                      </p>
                      <p className="text-[15px] font-medium text-[#303030] tracking-wide" data-id="element-770">
                        {newBeneficiaryAccount}
                      </p>
                    </div>
                    <div className="bg-[#FDF3ED] rounded-xl px-3 py-1.5" data-id="element-771">
                      <p className="text-[11px] font-bold text-[#661806]" data-id="element-772">
                        {newBeneficiaryBank}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-auto" data-id="element-773">
                <p className="text-[14px] font-medium text-[#661806] mb-3" data-id="element-774">
                  Set a Nickname for this Beneficiary (Optional)
                </p>
                <div className="relative" data-id="element-775">
                  <Edit3Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#818285]" data-id="element-776" />
                  <input type="text" value={newBeneficiaryNickname} onChange={e => setNewBeneficiaryNickname(e.target.value)} placeholder="e.g. Mom, Landlord" className="w-full bg-[#F5EBE6] rounded-2xl py-4 pl-12 pr-4 text-[15px] text-[#303030] placeholder:text-[#818285] outline-none" data-id="element-777" />
                </div>
              </div>

              <button onClick={() => {
              const newRecipient: Recipient = {
                id: Date.now().toString(),
                name: newBeneficiaryNickname || newBeneficiaryName,
                bank: newBeneficiaryBank,
                accountNumber: newBeneficiaryAccount,
                icon: BuildingIcon,
                color: '#F37024'
              };
              setBeneficiaries([...beneficiaries, newRecipient]);
              setSelectedRecipient(newRecipient);
              navigateTo('beneficiary_saved');
            }} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-[20px] shadow-md hover:bg-[#E06520] transition-colors flex items-center justify-center gap-2 mt-6" data-id="element-778">
                Save Beneficiary
              </button>
            </div>
          </motion.div>;
      case 'beneficiary_saved':
        return <motion.div key="beneficiary_saved" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen bg-[#FFF7F3] flex flex-col" data-id="element-779">
            <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-780">
              <div className="w-10" data-id="element-781" />
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-782" />
              <div className="w-10" data-id="element-783" />
            </div>

            <div className="px-6 flex-1 flex flex-col items-center justify-center pb-8" data-id="element-784">
              <motion.div initial={{
              scale: 0.5,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              type: 'spring',
              damping: 15,
              stiffness: 200,
              delay: 0.1
            }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6" data-id="element-785">
                <CheckCircleIcon className="w-14 h-14 text-[#22C55E]" strokeWidth={1.5} data-id="element-786" />
              </motion.div>

              <motion.h1 initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.25
            }} className="text-[28px] font-bold text-[#303030] text-center mb-2" data-id="element-787">
                Beneficiary Saved!
              </motion.h1>
              <motion.p initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.35
            }} className="text-[15px] text-[#818285] text-center max-w-[280px] mb-4" data-id="element-788">
                <span className="font-bold text-[#303030]" data-id="element-789">
                  {selectedRecipient?.name}
                </span>{' '}
                has been added to your beneficiary list.
              </motion.p>

              <motion.div initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.45
            }} className="bg-white rounded-[20px] p-5 shadow-sm w-full max-w-[320px] mb-auto" data-id="element-790">
                <div className="flex items-center gap-4" data-id="element-791">
                  <div className="w-12 h-12 rounded-full bg-[#FDF3ED] flex items-center justify-center shrink-0" data-id="element-792">
                    <UserIcon className="w-6 h-6 text-[#661806]" strokeWidth={2} data-id="element-793" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-794">
                    <p className="text-[16px] font-bold text-[#303030] truncate" data-id="element-795">
                      {selectedRecipient?.name}
                    </p>
                    <p className="text-[13px] text-[#818285] truncate" data-id="element-796">
                      {selectedRecipient?.bank} •{' '}
                      {selectedRecipient?.accountNumber}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.55
            }} className="w-full space-y-3 mt-8" data-id="element-797">
                <button onClick={() => navigateTo('enter_amount')} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-[20px] shadow-md hover:bg-[#E06520] transition-colors flex items-center justify-center gap-2" data-id="element-798">
                  Send Money Now
                  <ArrowRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-799" />
                </button>

                <button onClick={() => {
                setNewBeneficiaryName('');
                setNewBeneficiaryAccount('');
                setNewBeneficiaryRelationship('');
                setNewBeneficiaryBank('');
                setNewBeneficiaryNickname('');
                onBack();
              }} className="w-full py-4 bg-transparent text-[#661806] border-2 border-[#661806] font-bold text-[16px] rounded-[20px] hover:bg-[#661806]/5 transition-colors flex items-center justify-center" data-id="element-800">
                  Go Home
                </button>
              </motion.div>
            </div>
          </motion.div>;
      case 'enter_amount':
        return <motion.div key="enter_amount" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen flex flex-col bg-[#FFF7F3]" data-id="element-801">
            {/* Orange Header Section */}
            <div className="relative overflow-hidden px-6 pt-6 pb-8 rounded-b-[32px] shadow-sm" style={{
            background: 'linear-gradient(135deg, #F37024 0%, #D95A1A 100%)'
          }} data-id="element-802">
              {/* Animated background elements */}
              <motion.div className="absolute rounded-full border border-white/15" style={{
              width: 250,
              height: 250,
              top: '-40%',
              right: '-20%'
            }} animate={{
              scale: [1, 1.1, 0.95, 1],
              rotate: [0, 10, -5, 0]
            }} transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-803" />
              <motion.div className="absolute rounded-full" style={{
              width: 150,
              height: 150,
              bottom: '-20%',
              left: '-10%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
              filter: 'blur(10px)'
            }} animate={{
              x: [0, 20, -10, 0],
              y: [0, -15, 10, 0],
              opacity: [0.5, 0.8, 0.4, 0.5]
            }} transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-804" />

              {/* Header bar */}
              <div className="flex items-center justify-between mb-8 relative z-10" data-id="element-805">
                <button onClick={() => navigateTo('select_recipient', -1)} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center backdrop-blur-sm border border-white/10 hover:bg-white/25 transition-colors" data-id="element-806">
                  <ArrowLeftIcon className="w-5 h-5 text-white" strokeWidth={2} data-id="element-807" />
                </button>
                <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto brightness-0 invert" data-id="element-808" />
                <button onClick={() => setShowNotifications(true)} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center backdrop-blur-sm border border-white/10 hover:bg-white/25 transition-colors relative" data-id="element-809">
                  <BellIcon className="w-5 h-5 text-white" strokeWidth={2} data-id="element-810" />
                  <div className="absolute top-1 right-1 w-2 h-2 bg-[#F37024] rounded-full border border-white/20" data-id="element-811" />
                </button>
              </div>

              {/* Recipient Info */}
              <div className="relative z-10 flex flex-col items-center text-center" data-id="element-812">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-3 shadow-lg" data-id="element-813">
                  <UserIcon className="w-8 h-8 text-[#D95A1A]" strokeWidth={1.5} data-id="element-814" />
                </div>
                <h2 className="text-[22px] font-bold text-white mb-1" data-id="element-815">
                  {selectedRecipient?.name}
                </h2>
                <p className="text-[13px] text-white/80 font-medium tracking-wide" data-id="element-816">
                  {selectedRecipient?.bank} • ****{' '}
                  {selectedRecipient?.accountNumber.slice(-4)}
                </p>
              </div>
            </div>

            <div className="px-6 mt-8 flex-1 flex flex-col" data-id="element-817">
              {/* Amount Input */}
              <div className="flex flex-col items-center mb-10" data-id="element-818">
                <p className="text-[13px] font-bold text-[#818285] mb-3 uppercase tracking-widest" data-id="element-819">
                  Enter Amount
                </p>
                <div className="flex items-center justify-center gap-3 border-b-2 border-[#F5EBE6] pb-2 min-w-[240px] focus-within:border-[#F37024] transition-colors" data-id="element-820">
                  <span className="text-[28px] font-bold text-[#818285]" data-id="element-821">
                    Rs
                  </span>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" className="text-[56px] font-bold text-[#303030] bg-transparent outline-none w-[200px] text-center placeholder:text-[#303030]/20" autoFocus data-id="element-822" />
                </div>
              </div>

              {/* Quick Amounts */}
              <div className="mb-10" data-id="element-823">
                <div className="flex gap-3 justify-center" data-id="element-824">
                  {['1,000', '5,000', '10,000', '25,000'].map(val => <button key={val} onClick={() => setAmount(val.replace(',', ''))} className="px-4 py-2.5 bg-white border border-[#F0E8E3] rounded-full text-[14px] font-bold text-[#661806] shadow-sm hover:bg-[#F5EBE6] hover:border-[#F5EBE6] active:scale-95 transition-all" data-id="element-825">
                      +{val}
                    </button>)}
                </div>
              </div>

              {/* Purpose Dropdown */}
              <div className="mb-auto relative z-20" data-id="element-826">
                <div className="flex items-center justify-between mb-3" data-id="element-827">
                  <p className="text-[14px] font-bold text-[#303030]" data-id="element-828">
                    Purpose of Payment
                  </p>
                  <span className="text-[10px] font-bold text-[#F37024] bg-[#F37024]/10 px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-829">
                    Required
                  </span>
                </div>
                <div className="relative" data-id="element-830">
                  <button onClick={() => setShowPurposeDropdown(!showPurposeDropdown)} className="w-full bg-white border border-[#F0E8E3] rounded-2xl py-4 pl-5 pr-12 text-[15px] text-left text-[#303030] shadow-sm outline-none flex items-center hover:border-[#F37024]/50 transition-colors" data-id="element-831">
                    {purpose || <span className="text-[#818285]" data-id="element-832">Select Purpose</span>}
                    <ChevronDownIcon className="absolute right-5 w-5 h-5 text-[#818285]" data-id="element-833" />
                  </button>
                  <AnimatePresence data-id="element-834">
                    {showPurposeDropdown && <motion.div initial={{
                    opacity: 0,
                    y: -10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-[#F0E8E3] z-30 max-h-48 overflow-y-auto" data-id="element-835">
                        {PURPOSES.map(p => <button key={p} onClick={() => {
                      setPurpose(p);
                      setShowPurposeDropdown(false);
                    }} className="w-full text-left px-5 py-3.5 text-[15px] text-[#303030] hover:bg-[#F5EBE6] transition-colors border-b border-[#F0E8E3] last:border-0" data-id="element-836">
                            {p}
                          </button>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              {/* Pay Now Button */}
              <div className="mt-8 pb-8 relative z-10" data-id="element-837">
                <button onClick={() => navigateTo('mpin')} disabled={!amount || parseFloat(amount) <= 0 || !purpose} className="w-full py-4 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold text-[16px] rounded-[20px] shadow-[0_8px_20px_rgba(217,90,26,0.3)] hover:shadow-[0_8px_25px_rgba(217,90,26,0.4)] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none" data-id="element-838">
                  Pay Now
                  <ArrowRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-839" />
                </button>
              </div>
            </div>
          </motion.div>;
      case 'mpin':
        return <motion.div key="mpin" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen bg-[#FFF7F3] flex flex-col" data-id="element-840">
            <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-841">
              <button onClick={() => navigateTo('enter_amount', -1)} className="p-2 -ml-2 text-[#303030]" data-id="element-842">
                <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-843" />
              </button>
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-844" />
              <div className="w-10" data-id="element-845" />
            </div>

            <div className="px-6 mt-4 flex-1 flex flex-col items-center" data-id="element-846">
              <div className="w-12 h-12 rounded-full bg-[#FCECE3] flex items-center justify-center mb-6" data-id="element-847">
                <ShieldIcon className="w-6 h-6 text-[#D95A1A]" strokeWidth={1.5} data-id="element-848" />
              </div>

              <h1 className="text-[28px] font-bold text-[#303030] tracking-tight mb-2 text-center" data-id="element-849">
                {scanType === 'face' ? 'Face ID' : 'Touch ID'}
              </h1>
              <p className="text-[15px] text-[#818285] text-center mb-12 max-w-[280px]" data-id="element-850">
                {biometricPhase === 'scanning' ? `Scanning ${scanType === 'face' ? 'Face' : 'Fingerprint'}...` : biometricPhase === 'verified' ? `${scanType === 'face' ? 'Face' : 'Fingerprint'} Verified ✓` : 'Auto-filling MPIN...'}
              </p>

              {/* Biometric Animation Area */}
              <div className="relative w-48 h-48 flex items-center justify-center mb-16" data-id="element-851">
                {biometricPhase === 'verified' ? <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} className="w-24 h-24 rounded-full bg-[#22C55E]/20 flex items-center justify-center" data-id="element-852">
                    <CheckIcon className="w-12 h-12 text-[#22C55E]" strokeWidth={3} data-id="element-853" />
                  </motion.div> : scanType === 'face' ?
              // Face Scan Animation
              <div className="relative w-32 h-40" data-id="element-854">
                    <motion.div className="absolute inset-0 rounded-[40px] border-4 border-[#FCECE3]" data-id="element-855" />
                    <motion.div className="absolute inset-0 rounded-[40px] border-4 border-[#D95A1A]" style={{
                  clipPath: 'inset(0 0 80% 0)'
                }} animate={{
                  clipPath: ['inset(0 0 100% 0)', 'inset(0 0 0% 0)', 'inset(100% 0 0% 0)']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-856" />
                    {/* Scanning line */}
                    <motion.div className="absolute left-0 right-0 h-1 bg-[#D95A1A] shadow-[0_0_15px_rgba(217,90,26,0.8)]" animate={{
                  top: ['0%', '100%', '0%']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-857" />
                    {/* Pulsing background rings */}
                    <motion.div className="absolute inset-[-20px] rounded-[50px] border-2 border-[#D95A1A]/30" animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0, 0.5]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-858" />
                  </div> :
              // Thumb Scan Animation
              <div className="relative w-32 h-32 flex items-center justify-center" data-id="element-859">
                    <motion.div className="absolute inset-0 rounded-full border-2 border-[#D95A1A]/30" animate={{
                  scale: [1, 1.5, 2],
                  opacity: [0.8, 0.4, 0]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} data-id="element-860" />
                    <motion.div className="absolute inset-0 rounded-full border-2 border-[#D95A1A]/30" animate={{
                  scale: [1, 1.5, 2],
                  opacity: [0.8, 0.4, 0]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeOut',
                  delay: 1
                }} data-id="element-861" />
                    <FingerprintIcon className="w-20 h-20 text-[#D95A1A] relative z-10" strokeWidth={1.5} data-id="element-862" />
                    <motion.div className="absolute inset-0 bg-[#D95A1A]/20 rounded-full blur-xl" animate={{
                  opacity: [0.3, 0.7, 0.3]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-863" />
                  </div>}
              </div>

              {/* PIN Indicators */}
              <div className="flex justify-center gap-4 mt-auto pb-20" data-id="element-864">
                {[0, 1, 2, 3].map(index => <div key={index} className={`w-4 h-4 rounded-full transition-all duration-200 ${index < mpin.length ? 'bg-[#D95A1A] scale-110' : 'bg-[#FCECE3] border-2 border-[#E8D5CB]'}`} data-id="element-865" />)}
              </div>
            </div>
          </motion.div>;
      case 'processing':
        return <motion.div key="processing" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen bg-[#FFF7F3] flex flex-col items-center justify-center px-6" data-id="element-866">
            <div className="absolute top-6 left-0 right-0 flex justify-center" data-id="element-867">
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-868" />
            </div>

            <div className="relative w-32 h-32 mb-12" data-id="element-869">
              <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute inset-0 rounded-full border-4 border-[#F5EBE6] border-t-[#F37024]" data-id="element-870" />
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-871">
                <RefreshCwIcon className="w-8 h-8 text-[#F5CBA7]" strokeWidth={2} data-id="element-872" />
              </div>
            </div>

            <h2 className="text-[28px] font-bold text-[#303030] text-center mb-4" data-id="element-873">
              Processing your transfer...
            </h2>
            <p className="text-[16px] text-[#661806] text-center mb-12 max-w-[280px]" data-id="element-874">
              We're securing your funds and updating the ledger.
            </p>

            <div className="bg-white rounded-[20px] p-5 flex items-center gap-4 shadow-sm w-full max-w-[320px]" data-id="element-875">
              <div className="w-12 h-12 rounded-full bg-[#FDF3ED] flex items-center justify-center shrink-0" data-id="element-876">
                <WalletIcon className="w-6 h-6 text-[#661806]" strokeWidth={2} data-id="element-877" />
              </div>
              <div data-id="element-878">
                <p className="text-[11px] font-medium text-[#661806] uppercase tracking-wider mb-1" data-id="element-879">
                  Estimated Time
                </p>
                <p className="text-[16px] font-bold text-[#303030]" data-id="element-880">
                  ~ 5 seconds
                </p>
              </div>
            </div>
          </motion.div>;
      case 'success':
        return <motion.div key="success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen flex flex-col relative overflow-hidden" style={{
          background: 'linear-gradient(160deg, #F37024 0%, #D95A1A 40%, #661806 100%)'
        }} data-id="element-881">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-882">
              {/* Diagonal pattern overlay */}
              <div className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(255,255,255,0.3) 12px, rgba(255,255,255,0.3) 14px)'
            }} data-id="element-883" />

              {/* Large floating orb top-right */}
              <motion.div className="absolute rounded-full" style={{
              width: 300,
              height: 300,
              top: '-8%',
              right: '-15%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
              filter: 'blur(30px)'
            }} animate={{
              scale: [1, 1.3, 1],
              opacity: [0.4, 0.7, 0.4],
              x: [0, -20, 0],
              y: [0, 15, 0]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-884" />

              {/* Warm glow bottom-left */}
              <motion.div className="absolute rounded-full" style={{
              width: 250,
              height: 250,
              bottom: '-5%',
              left: '-10%',
              background: 'radial-gradient(circle, rgba(232,148,58,0.2) 0%, transparent 70%)',
              filter: 'blur(35px)'
            }} animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} data-id="element-885" />

              {/* Floating confetti particles */}
              {[...Array(12)].map((_, i) => <motion.div key={i} className="absolute rounded-full" style={{
              width: i % 3 === 0 ? 6 : i % 3 === 1 ? 4 : 8,
              height: i % 3 === 0 ? 6 : i % 3 === 1 ? 4 : 8,
              background: i % 2 === 0 ? 'rgba(255,255,255,0.4)' : 'rgba(232,148,58,0.5)',
              top: `${10 + i * 7 % 80}%`,
              left: `${5 + i * 11 % 90}%`
            }} animate={{
              y: [0, -40 - i * 5, 0],
              x: [0, i % 2 === 0 ? 15 : -15, 0],
              opacity: [0, 0.9, 0],
              scale: [0, 1.2, 0],
              rotate: [0, 180, 360]
            }} transition={{
              duration: 3 + i % 3,
              repeat: Infinity,
              delay: i * 0.4,
              ease: 'easeInOut'
            }} data-id="element-886" />)}

              {/* Rotating ring */}
              <motion.div className="absolute" style={{
              width: 500,
              height: 500,
              top: '50%',
              left: '50%',
              marginLeft: -250,
              marginTop: -250,
              borderRadius: '50%',
              background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.06), transparent, rgba(255,255,255,0.04), transparent)'
            }} animate={{
              rotate: [0, 360]
            }} transition={{
              duration: 25,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-887" />
            </div>

            {/* Confetti Burst */}
            <div className="absolute inset-0 pointer-events-none z-50 overflow-hidden" data-id="element-888">
              {[...Array(40)].map((_, i) => {
              const colors = ['#F37024', '#FFD700', '#22C55E', '#FFFFFF', '#661806', '#E8943A', '#FF6B6B', '#4ECDC4', '#FFE66D', '#FF8C42'];
              const color = colors[i % colors.length];
              const isRect = i % 3 !== 0;
              const startX = 50 + (Math.random() - 0.5) * 20;
              const endX = startX + (Math.random() - 0.5) * 120;
              const size = 6 + Math.random() * 8;
              return <motion.div key={`confetti-${i}`} className="absolute" style={{
                width: isRect ? size : size * 0.8,
                height: isRect ? size * 0.5 : size * 0.8,
                borderRadius: isRect ? 2 : '50%',
                backgroundColor: color,
                left: `${startX}%`,
                top: '15%'
              }} initial={{
                opacity: 1,
                scale: 0,
                y: 0,
                x: 0,
                rotate: 0
              }} animate={{
                opacity: [1, 1, 1, 0],
                scale: [0, 1.2, 1, 0.6],
                y: [0, -80 - Math.random() * 120, 200 + Math.random() * 500, 600 + Math.random() * 300],
                x: [(Math.random() - 0.5) * 40, (endX - startX) * 2, (endX - startX) * 3, (endX - startX) * 4],
                rotate: [0, 360 + Math.random() * 720, 720 + Math.random() * 360, 1080]
              }} transition={{
                duration: 2.5 + Math.random() * 1.5,
                delay: 0.1 + Math.random() * 0.4,
                ease: [0.25, 0.46, 0.45, 0.94],
                times: [0, 0.15, 0.6, 1]
              }} data-id="element-889" />;
            })}
            </div>

            {/* Header */}
            <div className="px-6 pt-6 pb-3 flex items-center justify-between relative z-10" data-id="element-890">
              <button onClick={onBack} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm" data-id="element-891">
                <XIcon className="w-5 h-5 text-white" strokeWidth={2} data-id="element-892" />
              </button>
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto brightness-0 invert" data-id="element-893" />
              <div className="w-10" data-id="element-894" />
            </div>

            {/* Success Header */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="flex flex-col items-center text-center px-6 mb-5 relative z-10" data-id="element-895">
              {/* Pulsing rings */}
              <div className="relative mb-4" data-id="element-896">
                <motion.div className="absolute inset-0 rounded-full border-2 border-white/40" animate={{
                scale: [1, 1.6, 2],
                opacity: [0.6, 0, 0]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeOut'
              }} data-id="element-897" />
                <motion.div className="absolute inset-0 rounded-full border-2 border-white/40" animate={{
                scale: [1, 1.6, 2],
                opacity: [0.6, 0, 0]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeOut',
                delay: 1
              }} data-id="element-898" />
                <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                damping: 12,
                stiffness: 200,
                delay: 0.3
              }} className="w-16 h-16 rounded-full bg-white flex items-center justify-center relative z-10 shadow-lg" data-id="element-899">
                  <CheckIcon className="w-9 h-9 text-[#22C55E]" strokeWidth={3} data-id="element-900" />
                </motion.div>
              </div>
              <h1 className="text-[20px] font-bold text-white mb-1" data-id="element-901">
                Transaction Successful
              </h1>
              <motion.h2 initial={{
              scale: 0.5,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.5,
              type: 'spring',
              damping: 15
            }} className="text-[44px] font-extrabold text-white leading-none" data-id="element-902">
                Rs.{' '}
                <span className="text-white" data-id="element-903">
                  {parseFloat(amount).toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
                </span>
              </motion.h2>
            </motion.div>

            {/* Receipt Card */}
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="mx-5 flex-1 flex flex-col relative z-10" data-id="element-904">
              <div className="bg-white rounded-t-[24px] rounded-b-none flex-1 flex flex-col relative overflow-hidden shadow-[0_-4px_30px_rgba(0,0,0,0.15)]" data-id="element-905">
                {/* Shimmer sweep */}
                <motion.div className="absolute top-0 h-full w-1/3 z-20 pointer-events-none" style={{
                background: 'linear-gradient(105deg, transparent 30%, rgba(243,112,36,0.08) 50%, transparent 70%)',
                transform: 'skewX(-15deg)'
              }} animate={{
                left: ['-40%', '140%']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 4,
                ease: 'easeInOut'
              }} data-id="element-906" />

                {/* Zig-zag top edge */}
                <div className="absolute top-0 left-0 right-0 h-3 flex overflow-hidden -mt-1" data-id="element-907">
                  {[...Array(25)].map((_, i) => <div key={i} className="w-4 h-4 bg-[#D95A1A] rounded-full transform -translate-y-2 shrink-0" data-id="element-908" />)}
                </div>

                <div className="px-6 pt-6 pb-5 flex-1 flex flex-col" data-id="element-909">
                  {/* Transaction Details */}
                  <div className="mb-5" data-id="element-910">
                    <h3 className="text-[13px] font-extrabold text-[#661806] uppercase tracking-[0.15em] mb-3" data-id="element-911">
                      Transaction Details
                    </h3>
                    <div className="space-y-2.5" data-id="element-912">
                      <div className="flex justify-between items-center" data-id="element-913">
                        <p className="text-[13px] text-[#818285]" data-id="element-914">TID</p>
                        <p className="text-[13px] font-bold text-[#303030] tracking-wide" data-id="element-915">
                          FT-992837465
                        </p>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-916">
                        <p className="text-[13px] text-[#818285]" data-id="element-917">
                          Date & Time
                        </p>
                        <p className="text-[13px] font-bold text-[#303030]" data-id="element-918">
                          23 Apr 2026, 02:45PM
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-[#F0E8E3] mb-5" data-id="element-919" />

                  {/* Amount Details */}
                  <div className="mb-5" data-id="element-920">
                    <h3 className="text-[13px] font-extrabold text-[#661806] uppercase tracking-[0.15em] mb-3" data-id="element-921">
                      Amount Details
                    </h3>
                    <div className="space-y-2.5" data-id="element-922">
                      <div className="flex justify-between items-center" data-id="element-923">
                        <p className="text-[13px] text-[#818285]" data-id="element-924">
                          Amount Paid
                        </p>
                        <p className="text-[13px] font-bold text-[#303030]" data-id="element-925">
                          {parseFloat(amount).toLocaleString('en-US', {
                          minimumFractionDigits: 2
                        })}
                        </p>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-926">
                        <p className="text-[13px] text-[#818285]" data-id="element-927">
                          Service Fee (Incl. Tax)
                        </p>
                        <p className="text-[13px] font-bold text-[#303030]" data-id="element-928">
                          00.00
                        </p>
                      </div>
                      <div className="h-px bg-[#F0E8E3]" data-id="element-929" />
                      <div className="flex justify-between items-center" data-id="element-930">
                        <p className="text-[14px] font-bold text-[#303030]" data-id="element-931">
                          Total Amount
                        </p>
                        <p className="text-[14px] font-extrabold text-[#661806]" data-id="element-932">
                          {parseFloat(amount).toLocaleString('en-US', {
                          minimumFractionDigits: 2
                        })}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-[#F0E8E3] mb-5" data-id="element-933" />

                  {/* Additional Information */}
                  <div className="relative mb-4" data-id="element-934">
                    <h3 className="text-[13px] font-extrabold text-[#661806] uppercase tracking-[0.15em] mb-3" data-id="element-935">
                      Additional Information
                    </h3>
                    <div className="space-y-2.5" data-id="element-936">
                      <div className="flex justify-between items-center" data-id="element-937">
                        <p className="text-[13px] text-[#818285]" data-id="element-938">
                          Receiver Title
                        </p>
                        <p className="text-[13px] font-bold text-[#303030]" data-id="element-939">
                          {selectedRecipient?.name}
                        </p>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-940">
                        <p className="text-[13px] text-[#818285]" data-id="element-941">
                          Receiver Acc. No.
                        </p>
                        <p className="text-[13px] font-bold text-[#303030]" data-id="element-942">
                          {selectedRecipient?.accountNumber}
                        </p>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-943">
                        <p className="text-[13px] text-[#818285]" data-id="element-944">Channel</p>
                        <p className="text-[13px] font-bold text-[#303030]" data-id="element-945">
                          IBFT
                        </p>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-946">
                        <p className="text-[13px] text-[#818285]" data-id="element-947">Bank</p>
                        <p className="text-[13px] font-bold text-[#303030]" data-id="element-948">
                          {selectedRecipient?.bank}
                        </p>
                      </div>
                    </div>

                    {/* PAID Stamp Watermark */}
                    <motion.div initial={{
                    scale: 0,
                    rotate: -30,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    rotate: -12,
                    opacity: 0.12
                  }} transition={{
                    delay: 0.8,
                    type: 'spring',
                    damping: 12,
                    stiffness: 150
                  }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" data-id="element-949">
                      <div className="w-28 h-28 rounded-full border-[4px] border-[#22C55E] flex items-center justify-center" data-id="element-950">
                        <span className="text-[28px] font-black text-[#22C55E] tracking-[0.2em]" data-id="element-951">
                          PAID
                        </span>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Zig-zag bottom edge */}
                <div className="h-3 flex overflow-hidden -mb-1" data-id="element-952">
                  {[...Array(25)].map((_, i) => <div key={i} className="w-4 h-4 bg-[#F37024] rounded-full transform translate-y-2 shrink-0" style={{
                  background: 'linear-gradient(160deg, #F37024, #661806)'
                }} data-id="element-953" />)}
                </div>
              </div>
            </motion.div>

            {/* Bottom Buttons */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7,
            duration: 0.4
          }} className="px-5 py-5 flex gap-3 relative z-10" data-id="element-954">
              <button className="flex-1 py-4 bg-white/15 backdrop-blur-sm text-white border border-white/30 font-bold text-[15px] rounded-[20px] flex items-center justify-center gap-2 active:scale-[0.97] transition-transform" data-id="element-955">
                <ShareIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-956" />
                Share
              </button>
              <button onClick={onBack} className="flex-1 py-4 bg-white text-[#661806] font-bold text-[15px] rounded-[20px] shadow-lg flex items-center justify-center gap-2 active:scale-[0.97] transition-transform" data-id="element-957">
                Dashboard
                <ArrowRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-958" />
              </button>
            </motion.div>
          </motion.div>;
    }
  };
  return <div className="w-full min-h-screen bg-[#FFF7F3] overflow-hidden" data-id="element-959">
      <AnimatePresence mode="wait" initial={false} custom={direction} data-id="element-960">
        {renderStep()}
      </AnimatePresence>

      {/* Notifications Panel */}
      <AnimatePresence data-id="element-961">
        {showNotifications && <NotificationPanel onClose={() => setShowNotifications(false)} data-id="element-962" />}
      </AnimatePresence>
    </div>;
}